import React, { useMemo } from 'react';
import { Card, Col, Row, Typography } from 'antd';
import type {
  RouteViewModelDocuments,
  RouteViewModelExecution,
  RouteViewModelFinanceAggregatedStatus,
  RouteViewModelRoutePenalty,
} from '../../../types/routeViewModel';

const { Text } = Typography;

function translateFinanceStatus(status: string | null | undefined): string {
  const normalized = String(status || '').trim().toUpperCase();
  if (!normalized) return '—';
  if (normalized === 'OVERDUE') return 'Просрочено';
  if (normalized === 'NOT_PAID' || normalized === 'OPEN') return 'Не оплачено';
  if (normalized === 'PAID' || normalized === 'OK') return 'Оплачено';
  if (normalized === 'PARTIAL' || normalized === 'PARTIALLY_PAID') return 'Частично';
  return status || '—';
}

function formatRub(value: number): string {
  return `${value.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ₽`;
}

type KpiTileProps = {
  title: string;
  value: React.ReactNode;
  subtitle?: React.ReactNode;
};

const KpiTile: React.FC<KpiTileProps> = ({ title, value, subtitle }) => (
  <Card size="small" style={{ height: '100%' }}>
    <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
      {title}
    </Text>
    <Text strong style={{ display: 'block', fontSize: 16, lineHeight: 1.3 }}>
      {value}
    </Text>
    {subtitle ? (
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 6 }}>
        {subtitle}
      </Text>
    ) : null}
  </Card>
);

export interface RouteKpiSummaryBlockProps {
  execution: RouteViewModelExecution;
  routePenalties: RouteViewModelRoutePenalty[];
  financeStatus: RouteViewModelFinanceAggregatedStatus | null;
  documents: RouteViewModelDocuments;
}

const RouteKpiSummaryBlock: React.FC<RouteKpiSummaryBlockProps> = ({
  execution,
  routePenalties,
  financeStatus,
  documents,
}) => {
  const overdueTasks = useMemo(
    () => execution.tasks.filter((task) => String(task.status || '').toUpperCase() === 'OVERDUE').length,
    [execution.tasks]
  );

  const penaltyTotal = useMemo(
    () => routePenalties.reduce((sum, item) => sum + (Number(item.calculatedAmount) || 0), 0),
    [routePenalties]
  );

  const satisfiedDocuments = useMemo(
    () => documents.requirements.filter((item) => item.status === 'SATISFIED').length,
    [documents.requirements]
  );

  return (
    <Card size="small" title="Сводка" style={{ marginBottom: 16 }}>
      <Row gutter={[12, 12]}>
        <Col xs={24} md={12} xl={6}>
          <KpiTile
            title="Задачи"
            value={execution.tasks.length}
            subtitle={`${overdueTasks} просрочено`}
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <KpiTile
            title="Штрафы"
            value={routePenalties.length}
            subtitle={routePenalties.length > 0 ? formatRub(penaltyTotal) : '0 ₽'}
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <KpiTile
            title="Финансы"
            value={translateFinanceStatus(financeStatus?.overallStatus)}
            subtitle={
              financeStatus
                ? `Вх: ${translateFinanceStatus(financeStatus.receivableStatus)} / Исх: ${translateFinanceStatus(financeStatus.payableStatus)}`
                : '—'
            }
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <KpiTile
            title="Документы"
            value={`${satisfiedDocuments} / ${documents.requirements.length}`}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default RouteKpiSummaryBlock;
